/**
 * EDL (Edit Decision List) — the single source of truth for a shorts project.
 *
 * An EDL is an immutable, serializable document. The ONLY way to mutate it is
 * through the reducer (`apply`) with a serializable {@link EditCommand}. Both the
 * human UI and the AI agent share that one command bus — there is no second path.
 *
 * Frame-based model (Remotion-style), fixed 30fps, 9:16 (1080x1920).
 */

export const FPS = 30 as const;
export const CANVAS_WIDTH = 1080 as const;
export const CANVAS_HEIGHT = 1920 as const;

/** Default duration for an sfx item when the command does not specify one (1s). */
export const DEFAULT_SFX_FRAMES = FPS;

/** A slice of a source media asset, in source-time seconds. */
export interface AssetRef {
  /** Media asset id — points at BOTH the proxy (preview) and full (export) renditions. */
  assetId: string;
  /** Start offset within the source, seconds. */
  sourceInSec: number;
  /** End offset within the source, seconds. (exclusive) */
  sourceOutSec: number;
}

export interface BaseItem {
  /** Deterministic id (see ids.ts). Never `Math.random`/`Date.now` derived. */
  id: string;
  /** Start position on the timeline, in frames. */
  from: number;
  /** Length on the timeline, in frames. Always > 0. */
  durationInFrames: number;
}

/** Normalized crop rect over the source frame (0..1 of source width/height). */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Caption styling. Every field here MUST be expressible by the export renderer
 * (libass). Do not add fields the preview can render but export cannot — that
 * breaks the parity contract (see docs §6.1).
 */
export interface CaptionStyle {
  fontFamily: string;
  /** Font size in px, measured against the 1080x1920 canvas. */
  fontSizePx: number;
  /** Primary fill, #RRGGBB. */
  primaryColor: string;
  /** Outline/border color, #RRGGBB. */
  outlineColor: string;
  /** Outline width in px. */
  outlineWidth: number;
  bold: boolean;
  /** Vertical anchor within the canvas. */
  alignment: 'top' | 'center' | 'bottom';
  /** Vertical margin from the anchored edge, px (ignored for 'center'). */
  marginVPx: number;
}

export const DEFAULT_CAPTION_STYLE: CaptionStyle = {
  fontFamily: 'Pretendard',
  fontSizePx: 72,
  primaryColor: '#FFFFFF',
  outlineColor: '#000000',
  outlineWidth: 6,
  bold: true,
  alignment: 'bottom',
  marginVPx: 220,
};

/**
 * Layout preset — chooses where the video and the branding/title overlays sit on
 * the fixed 1080x1920 canvas. The canvas size never changes; the preset only
 * decides placement (see {@link ./layout.computeLayoutSlots}).
 * - `basic`    — 16:9 video letterboxed mid-canvas, logo/channel/title above,
 *                subtitle below (spec "기본", 1080x608).
 * - `expanded` — video enlarged to fill the width and cropped, title on top,
 *                subtitle overlaid, logo/channel below (spec "확대").
 * - `bands`    — **자동 생성 전용.** 검정 배경 + 상단 후킹 제목 밴드 + 전폭 흰 자막띠 +
 *                4:3 가운데크롭 영상. 사람이 고르는 목록에서는 빠진다(아래 참조).
 */
export const ALL_LAYOUT_PRESETS = ['full', 'basic', 'expanded', 'bands'] as const;

export type LayoutPreset = (typeof ALL_LAYOUT_PRESETS)[number];

/**
 * 자동 생성 파이프라인 전용 프리셋. 사람이 에디터에서 고를 수 있는 것이 아니다 —
 * 밴드 좌표가 고정이라 사람이 손으로 조정할 여지가 없고, 그 대신 제목 폰트가 자동으로
 * 줄어든다(`title-fit.ts`).
 *
 * 🔴 «허용되는 값» 과 «고를 수 있는 값» 을 가르는 이유: 전자는 검증(zod)·렌더러가 쓰고
 *    후자는 UI 가 쓴다. 한 목록으로 두면 자동 전용 프리셋이 사람 UI 에 노출되고, 반대로
 *    UI 목록으로 검증하면 파이프라인이 만든 EDL 이 **조용히 거부된다**.
 */
export const AUTO_ONLY_LAYOUT_PRESETS = ['bands'] as const satisfies readonly LayoutPreset[];

/** 사람이 에디터에서 고를 수 있는 프리셋. `ALL_LAYOUT_PRESETS` 에서 «유도» 한다. */
export const LAYOUT_PRESETS: readonly LayoutPreset[] = ALL_LAYOUT_PRESETS.filter(
  (p) => !(AUTO_ONLY_LAYOUT_PRESETS as readonly string[]).includes(p)
);

export const DEFAULT_LAYOUT: LayoutPreset = 'full';

/**
 * Default styling for the project title overlay. Placement fields
 * (`alignment`/`marginVPx`) are ignored — the layout preset positions the title.
 */
export const DEFAULT_TITLE_STYLE: CaptionStyle = {
  fontFamily: 'Pretendard',
  fontSizePx: 64,
  primaryColor: '#FFFFFF',
  outlineColor: '#000000',
  outlineWidth: 5,
  bold: true,
  alignment: 'top',
  marginVPx: 0,
};

/**
 * Project title overlay — per-project text, edited by both the human UI and the
 * AI agent. Its on-canvas position comes from the layout preset, not the style.
 */
export interface TitleOverlay {
  text: string;
  style: CaptionStyle;
}

/**
 * How a source frame is fitted into the 9:16 output when its aspect ratio
 * differs (e.g. a 16:9 landscape stream). Applies to full-frame clips on the
 * `video` track; ignored for `broll` clips, which are placed by `rect`.
 * - `blur`   — whole frame centered to width, letterbox filled with a blurred,
 *              zoomed copy of the same frame (default).
 * - `cover`  — crop a 9:16 region (see `crop`) and fill the canvas.
 * - `contain`— whole frame centered to width, black letterbox top/bottom.
 */
export type ClipFit = 'blur' | 'cover' | 'contain';

export interface ClipItem extends BaseItem {
  type: 'clip';
  source: AssetRef;
  /**
   * 9:16 crop over the source (0..1). Absent → 렌더러가 `fit` 규칙대로 담는다
   * (`cover` 면 **가운데** 크롭).
   *
   * ⚠️ 예전 주석은 «renderer decides (saliency)» 라고 적혀 있었는데 **사실이 아니었다** —
   * 렌더러에 saliency 는 없다(구현은 `packages/shorts-testbed` 에만 있고 연결된 적 없다).
   * 어디를 남길지는 `framing.ts` 가 이 값을 써서 정한다.
   */
  crop?: CropRect;
  /** How to fit a differently-shaped source into 9:16. Absent → `'blur'`. */
  fit?: ClipFit;
  /** Linear gain 0..1. Absent → 1. */
  volume?: number;
  /**
   * Overlay placement on the output canvas (0..1), for B-roll clips on a `broll`
   * track. Absent → full-frame. A small rect = picture-in-picture cutaway.
   */
  rect?: CropRect;
}

export interface CaptionItem extends BaseItem {
  type: 'caption';
  text: string;
  style: CaptionStyle;
}

export type TransitionKind = 'cut' | 'fade' | 'whip' | 'zoom';

export interface TransitionItem extends BaseItem {
  type: 'transition';
  kind: TransitionKind;
  afterItemId: string;
  beforeItemId: string;
}

export interface SfxItem extends BaseItem {
  type: 'sfx';
  sfxId: string;
  volume?: number;
}

/**
 * An animated GIF / short meme clip overlaid on top of the video, e.g. a KLIPY
 * reaction. Lives on an `overlay` track. `src` is a fully-resolved external
 * media URL (an allowlisted KLIPY host — see the renderer/API SSRF guard).
 */
export interface GifItem extends BaseItem {
  type: 'gif';
  /** Resolved absolute media URL (mp4 rendition). */
  src: string;
  /** Placement on the 1080x1920 canvas (0..1). Required. */
  rect: CropRect;
}

export type Item = ClipItem | CaptionItem | TransitionItem | SfxItem | GifItem;

export type TrackKind = 'video' | 'caption' | 'sfx' | 'broll' | 'overlay';

/** Which item types a track of a given kind may hold. */
export const TRACK_ITEM_TYPES: Record<TrackKind, ReadonlyArray<Item['type']>> = {
  video: ['clip', 'transition'],
  caption: ['caption'],
  sfx: ['sfx'],
  broll: ['clip'],
  overlay: ['gif'],
};

export interface Track {
  id: string;
  kind: TrackKind;
  /** Invariant: sorted by `from` asc, ties broken by `id`. (see reducer normalize) */
  items: Item[];
}

export interface Edl {
  version: 1;
  fps: typeof FPS;
  width: typeof CANVAS_WIDTH;
  height: typeof CANVAS_HEIGHT;
  /** Layout preset: video + overlay placement on the canvas (see layout.ts). */
  layout: LayoutPreset;
  /** Project title overlay. Absent → no title is drawn. */
  title?: TitleOverlay;
  /** Derived cache: max(item.from + item.durationInFrames) across tracks. */
  durationInFrames: number;
  /**
   * Monotonic counter for deterministic id generation. Never decremented, even
   * on remove/undo — guarantees ids are never reused, so a given command
   * sequence always yields a byte-identical EDL. (schema-v1 refinement)
   */
  seq: number;
  tracks: Track[];
}

/**
 * Default track ids — stable so commands can target them without lookups.
 *
 * 자막은 두 레이어다:
 * - `caption-1` (speech) — 전사에서 기계 생성한 발화 자막. 화면 하단.
 * - `caption-2` (overlay) — 사람이 직접 넣는 강조/개입 자막. 화면 상단.
 * AI는 어느 쪽에도 쓰지 않는다 (docs/shorts-editor-quality-redesign.md §5 P0-2).
 */
export const DEFAULT_TRACK_IDS = {
  video: 'video-1',
  broll: 'broll-1',
  overlay: 'overlay-1',
  /** 발화 자막 (기계 생성). */
  caption: 'caption-1',
  /** 개입 자막 (사람 전용). */
  captionOverlay: 'caption-2',
  sfx: 'sfx-1',
} as const;

/**
 * 캔버스 위 자막 레이어 역할. 트랙 id로 판별한다 — `kind`는 둘 다 `'caption'`이라
 * 렌더러/프리뷰가 위치를 결정하려면 이 함수가 필요하다.
 */
export type CaptionLayer = 'speech' | 'overlay';

export function captionLayerOf(trackId: string): CaptionLayer {
  return trackId === DEFAULT_TRACK_IDS.captionOverlay ? 'overlay' : 'speech';
}

/**
 * 트랙 순서는 계약이다. 렌더러·프리뷰가 이 순서를 전제로 레이어를 쌓고,
 * 과거 EDL을 보강할 때(`ensureDefaultTracks`)도 이 순서로 끼워 넣는다.
 */
const CANONICAL_TRACKS: readonly { id: string; kind: TrackKind }[] = [
  { id: DEFAULT_TRACK_IDS.video, kind: 'video' },
  { id: DEFAULT_TRACK_IDS.broll, kind: 'broll' },
  { id: DEFAULT_TRACK_IDS.overlay, kind: 'overlay' },
  { id: DEFAULT_TRACK_IDS.caption, kind: 'caption' },
  { id: DEFAULT_TRACK_IDS.captionOverlay, kind: 'caption' },
  { id: DEFAULT_TRACK_IDS.sfx, kind: 'sfx' },
];

/** Create an empty EDL with the canonical track layout. */
export function createEmptyEdl(): Edl {
  return {
    version: 1,
    fps: FPS,
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
    layout: DEFAULT_LAYOUT,
    durationInFrames: 0,
    seq: 1,
    tracks: CANONICAL_TRACKS.map((t) => ({ id: t.id, kind: t.kind, items: [] })),
  };
}

/**
 * 과거 EDL에 없는 기본 트랙을 보강한다. `caption-2`처럼 나중에 추가된 트랙을 그냥
 * 쓰면 저장된 프로젝트에서 `TRACK_NOT_FOUND`로 죽으므로, 로드 직후 한 번 통과시킨다.
 *
 * 이미 있는 트랙은 **그대로 둔다** (아이템·순서 보존). 없는 것만 정규 순서 위치에 끼운다.
 * 참조 동일성을 유지해 불필요한 리렌더를 만들지 않는다 — 보강할 게 없으면 원본을 그대로 반환.
 */
export function ensureDefaultTracks(edl: Edl): Edl {
  const missing = CANONICAL_TRACKS.filter((t) => !edl.tracks.some((x) => x.id === t.id));
  if (missing.length === 0) return edl;
  const byId = new Map(edl.tracks.map((t) => [t.id, t]));
  const ordered: Track[] = [];
  for (const t of CANONICAL_TRACKS) {
    const existing = byId.get(t.id);
    ordered.push(existing ?? { id: t.id, kind: t.kind, items: [] });
    if (existing) byId.delete(t.id);
  }
  // 정규 목록에 없는 커스텀 트랙은 뒤에 보존한다 (버리면 데이터 손실).
  for (const t of edl.tracks) if (byId.has(t.id)) ordered.push(t);
  return { ...edl, tracks: ordered };
}

// ── Conversions ──────────────────────────────────────────────

/** Seconds → frames, rounded to nearest frame. */
export function secToFrames(sec: number): number {
  return Math.round(sec * FPS);
}

/** Frames → seconds. */
export function framesToSec(frames: number): number {
  return frames / FPS;
}

// ── Pure read helpers ────────────────────────────────────────

export interface ItemLocation {
  trackIndex: number;
  itemIndex: number;
  track: Track;
  item: Item;
}

/** Find an item by id across all tracks. Returns null if absent. */
export function locateItem(edl: Edl, itemId: string): ItemLocation | null {
  for (let trackIndex = 0; trackIndex < edl.tracks.length; trackIndex++) {
    const track = edl.tracks[trackIndex];
    if (!track) continue;
    for (let itemIndex = 0; itemIndex < track.items.length; itemIndex++) {
      const item = track.items[itemIndex];
      if (item && item.id === itemId) {
        return { trackIndex, itemIndex, track, item };
      }
    }
  }
  return null;
}

export function findTrack(edl: Edl, trackId: string): Track | null {
  return edl.tracks.find((t) => t.id === trackId) ?? null;
}

/** End frame (exclusive) of an item on the timeline. */
export function itemEnd(item: BaseItem): number {
  return item.from + item.durationInFrames;
}
